import React, { useState } from "react";
import hasPermission from "@/hooks/hasPermission";
import { Trash2, PenLine, Eye, Info, Archive, ArchiveRestore } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { noImg } from "@/assets";
import {
  DaysDialog,
  DetailsDialog,
  DeleteConfirmDialog,
  ImageDialog,
  CourseItemDialog,
  ExtraChargeDialog,
  ArchiveConfirmDialog,
} from "@/components/dialog/common-dialog";
import {
  deleteBanquetById,
  clearBanquetMessages,
  updateExistingBanquet,
  fetchAllBanquets,
} from "@/redux/slice/menu-manage-slice/special-menu/banquet-slice";
import { toast } from "react-hot-toast";
import { Switch } from "@/components/ui/switch";
import { toggleItemStatus } from "@/api/new-api/staff-management/menu-manage/toggle-status";
import useAuth from "@/hooks/useAuth";

function TableData({ data }) {
  const { user } = useAuth();
  const dispatch = useDispatch();
  const [details, setDetails] = useState(false);
  const [archiveDialog, setArchiveDialog] = useState(false);
  const [archiveActionType, setArchiveActionType] = useState("archive");
  const [imageDialog, setImageDialog] = useState(false);
  const [courseDialog, setCourseDialog] = useState(false);
  const [itemDialog, setItemDialog] = useState(false);
  const [dayDialog, setDayDialog] = useState(false);
  const [extraChargeDialog, setExtraChargeDialog] = useState(false);
  const { deleteLoading, updateLoading, requestStatus } = useSelector(
    (state) => state.menuManage.banquet
  );
  const [deleteDialog, setDeleteDialog] = useState(false);

  const closeArchiveDialog = () => {
    setArchiveDialog(false);
  };

  const archiveHandler = async (id, item, type) => {
    try {
      if (!id) {
        toast.error("ID is required.");
        return;
      }
      dispatch(clearBanquetMessages());
      const newStatus = type === "archive" ? "archived" : "active";
      const dataPayload = {
        type: "update",
        item_no: item?.item_no || null,
        name: item?.name || "",
        description: item?.description || "",
        price: item?.price || null,
        person_limit: item?.person_limit,
        vat: item?.vat ?? 0,
        status: newStatus,
        file_manager_id: item?.file_manager_id || item?.image?.id || undefined,
        days: item?.days?.map((d) => (typeof d === "object" ? d.id : d)) || [],
        extra_items: item?.extra_items?.map((ei) => ({
          item_id: ei?.item_id,
          item_type: ei?.item_type,
          variant_id: ei?.variant_id,
          file_manager_id: ei?.file_manager_id,
          quantity: ei?.quantity,
          extra_charge: ei?.extra_charge,
        })) || [],
        courses: item?.courses?.map((c) => ({
          name: c?.name,
          description: c?.description,
          max_selections: c?.max_selections,
          sort_order: c?.sort_order ?? 0,
          course_items: c?.course_items?.map((ci) => ({
            item_id: ci?.item_id,
            item_type: ci?.item_type,
            variant_id: ci?.variant_id,
            file_manager_id: ci?.file_manager_id,
            quantity: ci?.quantity,
            price: ci?.price,
          })) || [],
        })) || [],
        client_id: item?.client_id,
        restaurant_id: item?.restaurant_id,
      };
      const res = await dispatch(
        updateExistingBanquet({
          id,
          data: dataPayload,
        })
      );
      if (updateExistingBanquet.fulfilled.match(res)) {
        toast.success(`Banquet Night archived successfully!`);
      } else if (updateExistingBanquet.rejected.match(res)) {
        toast.error(res?.payload || `Failed to archive Banquet Night.`);
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong!");
    } finally {
      closeArchiveDialog();
    }
  };

  const deleteHandler = async (id) => {
    try {
      if (!id) {
        toast.error("ID is required.");
        return;
      }
      dispatch(clearBanquetMessages());
      const res = await dispatch(deleteBanquetById(id));
      if (deleteBanquetById.fulfilled.match(res)) {
        toast.success(`Banquet Night deleted successfully!`);
      } else if (deleteBanquetById.rejected.match(res)) {
        toast.error(res?.payload || `Failed to delete Banquet Night.`);
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong!");
    } finally {
      setDeleteDialog(false);
      setItemDialog(null);
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      const res = await toggleItemStatus("banquets", id);
      if (res.status === "success") {
        toast.success(res.message || "Banquet status updated successfully");
        dispatch(fetchAllBanquets(user?.restaurant_id));
      } else {
        toast.error(res.message || "Failed to toggle status.");
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong!");
    }
  };

  return (
    <>
      <Table className="w-full">
        <TableHeader>
          <TableRow>
            <TableHead>Item Name</TableHead>
            <TableHead>Course & Item</TableHead>
            <TableHead>Extra Item Charge</TableHead>
            <TableHead>Person Limit</TableHead>
            <TableHead>Price</TableHead>
            <TableHead>Available Days</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data && data.length > 0 ? (
            data.map((item, index) => (
              <TableRow key={index}>
                <TableCell className="font-medium">{item?.name}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    {item?.courses?.length}
                    <Eye
                      className="cursor-pointer"
                      onClick={() => {
                        setCourseDialog(true);
                        setItemDialog(item);
                      }}
                      size={18}
                    />
                  </div>
                </TableCell>

                <TableCell>
                  <div className="flex items-center gap-1">
                    {item?.extra_items?.length}
                    <Eye
                      className="cursor-pointer"
                      onClick={() => {
                        setExtraChargeDialog(true);
                        setItemDialog(item);
                      }}
                      size={18}
                    />
                  </div>
                </TableCell>
                <TableCell>{item?.person_limit || "0"}</TableCell>
                <TableCell>{item?.price || "0"}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    {item?.days?.length}
                    <Eye
                      className="cursor-pointer"
                      onClick={() => {
                        setDayDialog(true);
                        setItemDialog(item);
                      }}
                      size={18}
                    />
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {item?.status === "active" ? (
                      <>
                        <span className="text-cgreen text-base">Active</span>
                        <Switch
                          checked={true}
                          onCheckedChange={() => handleToggleStatus(item.id)}
                        />
                      </>
                    ) : item?.status === "inactive" ? (
                      <>
                        <span className="text-cred text-base">Inactive</span>
                        <Switch
                          checked={false}
                          onCheckedChange={() => handleToggleStatus(item.id)}
                        />
                      </>
                    ) : item?.status === "archived" ? (
                      <span className="text-cred text-base">Archived</span>
                    ) : (
                      <span className="text-cred text-base">Draft</span>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right justify-end flex gap-2 items-center">
                  <div className="flex items-center gap-2 h-8">
                    <Info
                      onClick={() => {
                        setDetails(true);
                        setItemDialog(item);
                      }}
                      className=" cursor-pointer"
                      size={18}
                    />
                    {hasPermission("back_office.menu_management.update_special_menu") && item?.status !== "archived" && (
                      <Link
                        state={item}
                        to={"/backoffice/menu/special-menu/banquet-night/update"}
                        className="cursor-pointer"
                      >
                        <PenLine className="text-cyellow" size={18} />
                      </Link>
                    )}

                    {hasPermission("back_office.menu_management.delete_special_menu") && item?.status !== "archived" && (
                      <span>
                        {item?.status === "draft" ? (
                          <Trash2
                            onClick={() => {
                              setItemDialog(item);
                              setDeleteDialog(true);
                            }}
                            className="text-cred cursor-pointer"
                            size={18}
                          />
                        ) : (
                          <Archive
                            onClick={() => {
                              setArchiveActionType("archive");
                              setItemDialog(item);
                              setArchiveDialog(true);
                            }}
                            className="text-cred cursor-pointer"
                            size={18}
                          />
                        )}
                      </span>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={8} className="text-center">
                No data available
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <CourseItemDialog
        items={itemDialog?.courses}
        isOpen={courseDialog}
        onClose={() => setCourseDialog(false)}
      />

      <DaysDialog
        items={itemDialog}
        isOpen={dayDialog}
        onClose={() => setDayDialog(false)}
      />

      <ExtraChargeDialog
        items={itemDialog?.extra_items}
        isOpen={extraChargeDialog}
        onClose={() => setExtraChargeDialog(false)}
      />

      <ImageDialog
        items={itemDialog}
        isOpen={imageDialog}
        onClose={() => setImageDialog(false)}
      />

      <DetailsDialog
        items={itemDialog}
        isOpen={details}
        onClose={() => setDetails(false)}
      />
      <ArchiveConfirmDialog
        isLoading={updateLoading}
        isOpen={archiveDialog}
        onClose={closeArchiveDialog}
        onConfirm={() => {
          archiveHandler(itemDialog.id, itemDialog, archiveActionType);
        }}
        title={archiveActionType === "archive" ? "Archive Banquet Night?" : "Restore Banquet Night?"}
        description={
          archiveActionType === "archive"
            ? `Are you sure you want to archive "${itemDialog?.name}"?`
            : `Are you sure you want to restore "${itemDialog?.name}"?`
        }
        isArchive={archiveActionType === "archive"}
      />
      <DeleteConfirmDialog
        isLoading={deleteLoading}
        isOpen={deleteDialog}
        onClose={() => {
          setDeleteDialog(false);
          setItemDialog(null);
        }}
        onConfirm={() => {
          deleteHandler(itemDialog.id);
        }}
        title="Delete Draft Banquet Night?"
        description={`Are you sure you want to delete the draft banquet night "${itemDialog?.name}"? This action cannot be undone.`}
      />
    </>
  );
}

export default TableData;
